/* 
 * Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
 * Distributed under the TruVideo proprietary license.
 */
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Upload, X, Image } from 'lucide-react';

/**
 * Generic file upload component.
 * Handles client-side validation, upload progress, preview, and clear.
 * All API logic lives in the uploadFn prop — this component is fully reusable.
 */
const FileUpload = ({
  id,
  label,
  description,
  currentUrl = '',
  onUploadComplete,
  onClear,
  uploadFn,
  disabled = false,
  accept = '.png, .jpg, .jpeg, .svg',
  maxSize = null,
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];

  const validate = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Only PNG, JPG/JPEG, and SVG files are allowed';
    }
    if (maxSize && file.size > maxSize) {
      return `File size must not exceed ${Math.round(maxSize / (1024 * 1024))}MB`;
    }
    return null;
  };

  const handleFile = async (file) => {
    if (!file) return;

    const validationError = validate(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setUploading(true);
    try {
      const result = await uploadFn(file);
      onUploadComplete(result.url);
    } catch (err) {
      const msg = err?.response?.data?.message;
      setError(Array.isArray(msg) ? msg[0] : (msg || 'Upload failed. Please try again.'));
    } finally {
      setUploading(false);
      // Reset input so the same file can be re-selected after clearing
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleInputChange = (e) => {
    handleFile(e.target.files?.[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled || uploading) return;
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleClear = () => {
    setError('');
    onClear();
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div>
      {label && (
        <label
          htmlFor={id}
          className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block mb-2"
        >
          {label}
        </label>
      )}

      {description && (
        <p className="text-base-content/40 text-[10px] font-medium mb-2">{description}</p>
      )}

      {/* Current image preview — shown when a URL exists and not uploading */}
      {currentUrl && !uploading && (
        <div className="mb-3 relative inline-block">
          <img
            src={currentUrl}
            alt={label || 'Uploaded image'}
            className="h-16 w-16 object-contain rounded-lg border border-base-300 bg-base-100 p-1"
            onError={(e) => { e.target.style.display = 'none'; }}
            onLoad={(e) => { e.target.style.display = 'block'; }}
          />
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              title="Remove image"
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-base-300 rounded-full flex items-center justify-center shadow-sm"
            >
              <X size={11} strokeWidth={3} />
            </button>
          )}
        </div>
      )}

      {/* Drop zone — hidden when disabled and a URL already exists */}
      {(!currentUrl || uploading) && (
        <div
          onDragOver={(e) => { e.preventDefault(); if (!disabled && !uploading) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !disabled && !uploading && inputRef.current?.click()}
          className={`
            relative flex flex-col items-center justify-center gap-2
            border-2 border-dashed rounded-xl px-4 py-6 transition-all
            ${disabled ? 'cursor-not-allowed opacity-50 bg-base-200 border-base-300' : ''}
            ${!disabled && !uploading ? 'cursor-pointer' : ''}
            ${dragOver ? 'border-primary bg-primary/5' : 'border-base-300 hover:border-primary/50 hover:bg-base-200/50'}
            ${uploading ? 'border-primary/30 bg-primary/5' : ''}
          `}
        >
          {uploading ? (
            <>
              <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
              <span className="text-xs font-bold text-base-content/50">Uploading...</span>
            </>
          ) : (
            <>
              <div className="w-8 h-8 bg-base-200 rounded-lg flex items-center justify-center">
                {dragOver ? (
                  <Image size={16} className="text-primary" />
                ) : (
                  <Upload size={16} className="text-base-content/30" />
                )}
              </div>
              <div className="text-center">
                <p className="text-xs font-bold text-base-content/50">
                  {dragOver ? 'Drop to upload' : 'Click or drag & drop'}
                </p>
                <p className="text-[10px] text-base-content/30 mt-0.5">
                  PNG, JPG/JPEG, SVG
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Replace button — shown when URL exists and not uploading */}
      {currentUrl && !uploading && !disabled && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="mt-2 flex items-center gap-1.5 text-[10px] font-bold text-base-content/40 hover:text-primary transition"
        >
          <Upload size={11} />
          Replace image
        </button>
      )}
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        disabled={disabled || uploading}
        onChange={handleInputChange}
        className="sr-only"
      />

      {error && (
        <p className="mt-1.5 text-xs font-medium text-error">{error}</p>
      )}
    </div>
  );
};

FileUpload.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  description: PropTypes.string,
  currentUrl: PropTypes.string,
  onUploadComplete: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  uploadFn: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  accept: PropTypes.string,
  maxSize: PropTypes.number,
};

export default FileUpload;
